import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Selection } from "../selection/Selection";
import type { Filter } from "./Filter";

export type LightType = "point" | "spot" | "directional";

export interface LightingEffectsParams {
  lightType: LightType;
  color: { r: number; g: number; b: number };
  intensity: number;
  ambient: number;
  positionX: number;
  positionY: number;
  height: number;
}

function clamp8(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : v;
}

export const LightingEffects: Filter<LightingEffectsParams> = {
  id: "lighting-effects",
  displayName: "Lighting Effects",
  defaultParams: {
    lightType: "point",
    color: { r: 255, g: 255, b: 255 },
    intensity: 50,
    ambient: 20,
    positionX: 50,
    positionY: 50,
    height: 50,
  },

  apply(input: PixelBuffer, params: LightingEffectsParams, _selection: Selection | null): PixelBuffer {
    const w = input.width;
    const h = input.height;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data;
    const dst = out.data;

    if (w === 0 || h === 0) { dst.set(src); return out; }

    const lx = (params.positionX / 100) * w;
    const ly = (params.positionY / 100) * h;
    const intensity = params.intensity / 100;
    const ambient = params.ambient / 100;
    const lightColor = params.color;
    const maxDist = Math.sqrt(w * w + h * h);

    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const o = (y * w + x) * 4;

        const dx = x - lx, dy = y - ly;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const normalizedDist = dist / maxDist;

        let lightFactor: number;
        if (params.lightType === "point") {
          lightFactor = Math.exp(-normalizedDist * 3) * intensity;
        } else if (params.lightType === "spot") {
          lightFactor = Math.max(0, 1 - normalizedDist * 2) * intensity;
        } else {
          lightFactor = intensity * 0.5;
        }

        const r = src[o]! / 255;
        const g = src[o + 1]! / 255;
        const b = src[o + 2]! / 255;

        const litR = r * (ambient + lightFactor * lightColor.r / 255);
        const litG = g * (ambient + lightFactor * lightColor.g / 255);
        const litB = b * (ambient + lightFactor * lightColor.b / 255);

        dst[o] = clamp8(Math.round(litR * 255));
        dst[o + 1] = clamp8(Math.round(litG * 255));
        dst[o + 2] = clamp8(Math.round(litB * 255));
        dst[o + 3] = src[o + 3]!;
      }
    }
    return out;
  },
};
